import React, { memo, useContext } from 'react';
import { FormikInput } from 'Components/frameworks/formik/FormikInput';
import { TemplateString } from 'Components/frameworks/template-string/TemplateString';
import { getPaymentProviderLabel } from 'Components/widgets/payment-info/payment-form/paymentProvidersUtils';
import { formatApiDate } from 'Utils/date/apiDate';
import { t } from 'Src/i18n';
import { PaymentInfoContext } from 'Components/widgets/payment-info/PaymentInfoContext';
import { useB2BPaymentViewContext } from 'Components/widgets/payment-info/b2b-payment-info/B2BPaymentViewContext';
type Props = {
  hasFullAmount: boolean;
  isDisabled: boolean;
  paymentProvider: PaymentProvider;
  rePaymentSchedule: RepaymentSchedule;
  isProviderSelectable: boolean;
  fees: string;
  totalAmount: string;
  totalAmountWithFees: string;
  depositAmountWithFees: string;
  paymentMethodCategoryWithFees: Array<string>;
  depositAmount: string;
};
export const GenericPaymentBlock = ({
  hasFullAmount,
  isDisabled,
  paymentProvider,
  isProviderSelectable,
  rePaymentSchedule,
  fees,
  totalAmount,
  totalAmountWithFees,
  depositAmount,
  depositAmountWithFees,
  paymentMethodCategoryWithFees,
}: Props) => {
  const { getB2BPaymentProviderLabel } = useB2BPaymentViewContext();
  const { isFreeDeposit } = useContext(PaymentInfoContext);
  const isTotalAmount = isFreeDeposit || hasFullAmount || !rePaymentSchedule;
  const displayWithFees = paymentMethodCategoryWithFees.includes(
    paymentProvider.categoryPaymentMethod,
  );
  const providerLabel =
    getB2BPaymentProviderLabel({
      paymentProvider,
      isTotalAmount,
    }) ||
    getPaymentProviderLabel({
      paymentProvider,
      isTotalAmount,
    });
  const PaymentProviderLabel = memo(() => (
    <TemplateString
      template={providerLabel}
      values={
        !isTotalAmount
          ? {
              depositAmount: displayWithFees ? depositAmountWithFees : depositAmount,
              balanceAmount: rePaymentSchedule.amount.formattedValue,
              balanceDate: formatApiDate(rePaymentSchedule.deadline, t('booking.form.dateFormat')),
            }
          : {
              totalAmount: displayWithFees ? totalAmountWithFees : totalAmount,
            }
      }
      valuesClassName={{
        depositAmount: 'font-bold text-sienna',
        balanceAmount: 'font-bold text-sienna',
        totalAmount: 'font-bold text-sienna',
        balanceDate: 'font-bold',
        fees: 'font-bold text-blueWave',
      }}
    />
  ));
  return (
    <div className="my-10">
      <input type="hidden" name="paymentProviderId" value={paymentProvider.id} />
      {isProviderSelectable ? (
        <FormikInput
          type="radio"
          name="selectorPaymentProviderId"
          data-testid={`radioInput-paymentProviderId-${paymentProvider.selectorId}`}
          isDisabled={isDisabled}
          value={paymentProvider.selectorId}
          label={
            <React.Fragment>
              <PaymentProviderLabel />{' '}
              {!!fees && displayWithFees && (
                <TemplateString
                  template={t('booking.banking.paymentProvider.feesApplied')}
                  className="text-12"
                  values={{
                    fees,
                  }}
                  valuesClassName={{
                    fees: 'font-bold text-blueWave',
                  }}
                />
              )}
            </React.Fragment>
          }
        />
      ) : (
        <div
          className="text-14"
          data-testid={`label-paymentProviderId-${paymentProvider.selectorId}`}
        >
          <PaymentProviderLabel />
        </div>
      )}
    </div>
  );
};
